import type { SituacaoPedido } from '@arena/shared/dominio';
import type { EncaixeDePedido } from '@arena/shared/schemas';
import { partesNoFuso } from '@arena/shared/tempo';
import type { Situacao } from '../ui/primitivos.jsx';

/**
 * Os textos de `#v-site` e `#m-pedido`. Funções puras, testadas em
 * `formatos.test.ts`.
 *
 * Toda hora passa pelo fuso DA ARENA — o pedido das 20h é às 20h na
 * recepção de Goiânia com o navegador em qualquer lugar.
 */

/** "há 12 min", "há 1 h", "ontem", "anteontem", "12/09" — a coluna Recebido. */
export function recebidoHa(recebidoEm: string, agora: Date, fuso: string): string {
  const minutos = Math.floor((agora.getTime() - Date.parse(recebidoEm)) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);

  const hoje = partesNoFuso(agora, fuso).data;
  const dia = partesNoFuso(recebidoEm, fuso).data;
  if (dia === hoje) return `há ${horas} h`;

  const dias = Math.round(
    (Date.parse(`${hoje}T12:00:00Z`) - Date.parse(`${dia}T12:00:00Z`)) / 86_400_000,
  );
  if (dias === 1) return 'ontem';
  if (dias === 2) return 'anteontem';
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
}

/** "Hoje 20h", "Amanhã 9h30", "Qui 21h", "02/10 19h" — o horário pedido. */
export function horarioPedido(inicio: string, agora: Date, fuso: string): string {
  const partes = partesNoFuso(inicio, fuso);
  const [h, m] = partes.hora.split(':');
  const hora = m === '00' ? `${Number(h)}h` : `${Number(h)}h${m}`;

  const hoje = partesNoFuso(agora, fuso).data;
  const dias = Math.round(
    (Date.parse(`${partes.data}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000,
  );
  if (dias === 0) return `Hoje ${hora}`;
  if (dias === 1) return `Amanhã ${hora}`;
  if (dias > 1 && dias < 7) return `${DIAS_CURTOS[partes.diaDaSemana]} ${hora}`;
  return `${partes.data.slice(8, 10)}/${partes.data.slice(5, 7)} ${hora}`;
}

const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** A pílula de situação: pendente em atenção, confirmado ok, recusado atrasado — como no protótipo. */
export function situacaoDoPedido(
  situacao: SituacaoPedido,
  motivoRecusa?: string | null,
): { situacao: Situacao; rotulo: string } {
  switch (situacao) {
    case 'Pendente':
      return { situacao: 'atencao', rotulo: 'Pendente' };
    case 'Confirmado':
      return { situacao: 'ok', rotulo: 'Confirmado' };
    case 'Recusado':
      return {
        situacao: 'atrasada',
        rotulo: motivoRecusa ? `Recusado · ${motivoRecusa}` : 'Recusado',
      };
  }
}

export const ROTULO_DO_ENCAIXE: Readonly<Record<EncaixeDePedido, string>> = {
  Experimental: 'Aula experimental',
  LocacaoAvulsa: 'Locação avulsa',
};

/** [1,2,3,4,5] → "seg a sex"; [0,6] → "dom e sáb"; [1,3] → "seg e qua". */
export function diasDaFaixa(dias: readonly number[]): string {
  const nomes = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
  const ordenados = [...new Set(dias)].sort((a, b) => a - b);
  if (ordenados.length === 7) return 'todos os dias';
  const seguidos =
    ordenados.length > 2 &&
    ordenados.every((d, i) => i === 0 || d === (ordenados[i - 1] ?? -2) + 1);
  if (seguidos)
    return `${nomes[ordenados[0] ?? 0]} a ${nomes[ordenados[ordenados.length - 1] ?? 0]}`;
  const lista = ordenados.map((d) => nomes[d] ?? '?');
  return lista.length <= 1
    ? lista.join('')
    : `${lista.slice(0, -1).join(', ')} e ${lista[lista.length - 1]}`;
}

/** "07h às 11h" — a faixa de uma promoção. */
export function faixaCurta(inicio: string, fim: string): string {
  const curta = (hora: string) => {
    const [h, m] = hora.split(':');
    return m === '00' ? `${h}h` : `${h}h${m}`;
  };
  return `${curta(inicio)} às ${curta(fim)}`;
}

/** "30%" → 30; "30" → 30; "12,5" → null (a promoção é em pontos inteiros). */
export function descontoDigitado(texto: string): number | null {
  const limpo = texto.replace(/[%\s]/g, '');
  if (!/^\d{1,2}$/.test(limpo)) return null;
  return Number(limpo);
}
